import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import BottomNav from "../components/BottomNav";
import { 
  Wine, ChevronLeft, Tag, ExternalLink, X, FileText,
  Percent, Gift, PartyPopper
} from "lucide-react";
import { Button } from "../components/ui/button";

const DiscountsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/bartender/discounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscounts(response.data);
    } catch (e) {
      console.error("Error fetching discounts:", e);
      // Use default discounts if API not ready
      setDiscounts(getDefaultDiscounts());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultDiscounts = () => [
    {
      id: "mobile-bars",
      title: "Mobile Bars for Your Parties",
      subtitle: "10% off with code POURCIRCLE",
      description: "Professional mobile bar service for weddings, corporate events, and private parties. Full bar setup, professional bartenders, and premium cocktails.",
      code: "POURCIRCLE",
      discount: "10%",
      icon: "party",
      color: "#FF6B35",
      merchant: "Elite Mobile Bars",
      pdf_url: null,
      contact: {
        phone: "(555) 123-4567",
        email: "info@elitemobilebars.com",
        website: "www.elitemobilebars.com"
      }
    },
    {
      id: "bar-supplies",
      title: "Premium Bar Supplies & Tools",
      subtitle: "15% off your first order",
      description: "Professional-grade bar tools, glassware, and supplies. Everything you need to craft the perfect cocktail.",
      code: "POURPRO15",
      discount: "15%",
      icon: "gift",
      color: "#9333EA",
      merchant: "BarTools Pro",
      pdf_url: null,
      contact: {
        phone: "(555) 234-5678",
        email: "orders@bartoolspro.com",
        website: "www.bartoolspro.com"
      }
    },
    {
      id: "insurance",
      title: "Bartender Liability Insurance",
      subtitle: "Special rates for PourCircle members",
      description: "Protect yourself with comprehensive liability coverage designed specifically for bartenders. Quick online quotes and instant coverage.",
      code: "POURCIRCLE",
      discount: "20%",
      icon: "percent",
      color: "#059669",
      merchant: "BarSafe Insurance",
      pdf_url: null,
      contact: {
        phone: "(555) 345-6789",
        email: "quotes@barsafeinsurance.com",
        website: "www.barsafeinsurance.com"
      }
    }
  ];

  const getIcon = (iconType) => {
    switch (iconType) {
      case "party": return <PartyPopper className="w-6 h-6" />;
      case "gift": return <Gift className="w-6 h-6" />;
      case "percent": return <Percent className="w-6 h-6" />;
      default: return <Tag className="w-6 h-6" />;
    }
  };

  if (!user || user.role !== "bartender") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-white/60">This page is for bartenders only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="discounts-page">
      {/* Header */}
      <header className="p-6 flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          className="text-white/60 hover:text-white"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <div className="flex items-center gap-2">
          <Tag className="w-6 h-6 text-primary" />
          <span className="text-xl font-bold text-white">Exclusive Discounts</span>
        </div>
      </header>

      <main className="px-6 pb-24 space-y-4">
        <p className="text-white/60 text-sm mb-6">
          Special offers exclusively for PourCircle bartenders. Tap a card to see details and how to redeem.
        </p>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {discounts.map((discount) => (
              <button
                key={discount.id}
                onClick={() => setSelectedDiscount(discount)}
                className="w-full glass-card-hover p-5 text-left relative overflow-hidden"
                data-testid={`discount-${discount.id}`}
              >
                <div 
                  className="absolute top-0 right-0 w-24 h-24 opacity-10 transform translate-x-8 -translate-y-8"
                  style={{ backgroundColor: discount.color, borderRadius: "50%" }}
                />
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${discount.color}20` }}
                  >
                    <span style={{ color: discount.color }}>
                      {getIcon(discount.icon)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg">{discount.title}</h3>
                    <p className="text-primary font-medium text-sm mt-1">{discount.subtitle}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-white/10 text-white/80 px-2 py-1 rounded">
                        {discount.merchant}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Discount Detail Modal */}
      {selectedDiscount && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center"
          onClick={() => setSelectedDiscount(null)}
        >
          <div 
            className="bg-card w-full max-w-lg rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-card p-6 border-b border-white/10 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">{selectedDiscount.title}</h2>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSelectedDiscount(null)}
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Discount Badge */}
              <div 
                className="w-full py-4 rounded-xl text-center"
                style={{ backgroundColor: `${selectedDiscount.color}20` }}
              >
                <p className="text-3xl font-bold" style={{ color: selectedDiscount.color }}>
                  {selectedDiscount.discount} OFF
                </p>
                <p className="text-white/60 text-sm mt-1">Use code: <span className="text-white font-mono font-bold">{selectedDiscount.code}</span></p>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-white/60 text-sm font-medium mb-2">About This Offer</h3>
                <p className="text-white">{selectedDiscount.description}</p>
              </div>

              {/* Merchant Info */}
              <div>
                <h3 className="text-white/60 text-sm font-medium mb-2">Merchant</h3>
                <p className="text-white font-semibold">{selectedDiscount.merchant}</p>
              </div>

              {/* Contact Information */}
              {selectedDiscount.contact && (
                <div>
                  <h3 className="text-white/60 text-sm font-medium mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    {selectedDiscount.contact.phone && (
                      <a 
                        href={`tel:${selectedDiscount.contact.phone}`}
                        className="block text-primary hover:underline"
                      >
                        {selectedDiscount.contact.phone}
                      </a>
                    )}
                    {selectedDiscount.contact.email && (
                      <a 
                        href={`mailto:${selectedDiscount.contact.email}`}
                        className="block text-primary hover:underline"
                      >
                        {selectedDiscount.contact.email}
                      </a>
                    )}
                    {selectedDiscount.contact.website && (
                      <a 
                        href={`https://${selectedDiscount.contact.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {selectedDiscount.contact.website}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* PDF Link */}
              {selectedDiscount.pdf_url && (
                <Button 
                  onClick={() => window.open(`${API}/files/${selectedDiscount.pdf_url}?auth=${token}`, '_blank')}
                  className="w-full"
                  style={{ backgroundColor: selectedDiscount.color }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Full Details (PDF)
                </Button>
              )}

              {/* Copy Code Button */}
              <Button 
                variant="outline"
                className="w-full border-white/20 text-white"
                onClick={() => {
                  navigator.clipboard.writeText(selectedDiscount.code);
                  // Could add toast here
                }}
              >
                Copy Discount Code
              </Button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default DiscountsPage;
